import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ExamPaperLayout from '../../../components/Exam/ExamPaperLayout';
import { examB2API } from '../../../services/api';
import { SCHREIBEN_TASKS as FALLBACK_TASKS } from '../../../data/exam/b2';

const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

const SchreibenPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTaskIdx, setActiveTaskIdx] = useState(0);
  const [texts, setTexts] = useState({});
  const [results, setResults] = useState({});
  const [finished, setFinished] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    examB2API.getTasks('schreiben')
      .then(({ tasks: t }) => setTasks(t))
      .catch(() => setTasks(FALLBACK_TASKS))
      .finally(() => setLoading(false));
  }, []);

  const task = tasks[activeTaskIdx];
  const totalTeile = tasks.length || 0;
  const isFirstTeil = activeTaskIdx === 0;
  const isLastTeil = activeTaskIdx === totalTeile - 1;
  const currentText = texts[task?.id] || '';
  const wordCount = countWords(currentText);
  const wordTarget = task?.wordTarget || { min: 0, max: 999 };
  const inRange = wordCount >= wordTarget.min;

  const handleSubmitTask = async () => {
    if (!task || !currentText.trim()) return;
    setSubmitting(true);
    try {
      const res = await examB2API.submitSchreiben(task.id, currentText);
      setResults((prev) => ({ ...prev, [task.id]: res.data.data }));
      if (activeTaskIdx < tasks.length - 1) {
        setActiveTaskIdx((i) => i + 1);
      } else {
        setFinished(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="exam-paper-root flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-zinc-400" />
      </div>
    );
  }

  if (finished) {
    const avgScore = Math.round(
      tasks.reduce((s, t) => s + (results[t.id]?.score ?? 0), 0) / (tasks.length || 1),
    );
    const passed = avgScore >= 60;

    return (
      <div className="exam-results-root">
        <div className="exam-results-paper">
          <div className="exam-results-header">
            <p className="exam-paper-brand">Goethe-Zertifikat B2</p>
            <p className="exam-paper-subbrand">Auswertung · Schreiben</p>
            <div className={`exam-score-badge ${passed ? 'passed' : 'failed'}`}>{avgScore}%</div>
          </div>
          {tasks.map((t) => {
            const r = results[t.id];
            const ok = r?.passed;
            return (
              <div key={t.id} className={`exam-review-item ${ok ? 'is-correct' : 'is-wrong'}`}>
                <div className="flex justify-between">
                  <strong>{t.title}</strong>
                  <span className={ok ? 'text-green-700' : 'text-red-700'}>{r?.score ?? 0}%</span>
                </div>
                <p className="mt-1 text-brand-text/70">
                  {r?.word_count ?? countWords(texts[t.id] || '')} Wörter
                  {r?.length_ok ? '' : ' — Wortzahl unter Minimum'}
                </p>
                {r?.hints?.map((h, i) => (
                  <p key={i} className="text-red-700 text-sm mt-1">• {h}</p>
                ))}
                {r?.grammar_errors?.slice(0, 5).map((e, i) => (
                  <p key={i} className="text-sm text-red-600 mt-1">{e.explanation_sq}</p>
                ))}
              </div>
            );
          })}
          <div className="exam-submit-bar px-8 pb-8">
            <button type="button" className="exam-btn-primary" onClick={() => { setFinished(false); setActiveTaskIdx(0); setResults({}); }}>
              Erneut versuchen
            </button>
            <button type="button" className="exam-btn-secondary" onClick={() => navigate('/exam/b2')}>
              Alle Module
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!task) return null;

  return (
    <ExamPaperLayout
      moduleKey="schreiben"
      moduleName="Schreiben"
      moduleDuration="75 Minuten"
      totalMinutes={75}
      timerRunning
      onExit={() => navigate('/exam/b2')}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-sans font-bold text-brand-text/70">
          Teil {activeTaskIdx + 1} von {totalTeile}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="exam-btn-secondary"
            onClick={() => setActiveTaskIdx((i) => Math.max(0, i - 1))}
            disabled={isFirstTeil}
          >
            ← Zurück
          </button>
          <button
            type="button"
            className="exam-btn-secondary"
            onClick={() => setActiveTaskIdx((i) => Math.min(totalTeile - 1, i + 1))}
            disabled={isLastTeil}
          >
            Weiter →
          </button>
        </div>
      </div>

      <div className="exam-teil-block">
        <h2 className="exam-teil-heading">Teil {task.teil}</h2>
        {task.arbeitzeit && (
          <p className="text-sm font-bold mb-3">{task.arbeitzeit}</p>
        )}

        <div className="border border-gray-500 p-4 mb-4 text-sm leading-relaxed exam-instruction">
          {task.prompt?.intro && <p>{task.prompt.intro}</p>}
          {task.prompt?.bulletPoints?.length > 0 && (
            <ul className="list-none pl-0 mt-3 space-y-2">
              {task.prompt.bulletPoints.map((bp, i) => (
                <li key={i} className="flex gap-2">
                  <span aria-hidden>▪</span>
                  <span>{bp}</span>
                </li>
              ))}
            </ul>
          )}
          {task.prompt?.hinweis && <p className="mt-3">{task.prompt.hinweis}</p>}
          {task.prompt?.bewertung && <p className="mt-3">{task.prompt.bewertung}</p>}
          {task.prompt?.wordNote && (
            <p className="mt-3 font-bold">{task.prompt.wordNote}</p>
          )}
        </div>

        <textarea
          value={currentText}
          onChange={(e) => setTexts((prev) => ({ ...prev, [task.id]: e.target.value }))}
          placeholder="Schreiben Sie hier Ihren Text …"
          className="w-full min-h-[280px] p-4 border-2 border-gray-800 text-sm leading-relaxed font-serif resize-y focus:outline-none bg-white"
        />

        <p className={`text-xs mt-2 font-sans font-bold ${inRange ? 'text-brand-text/80' : 'text-orange-700'}`}>
          {wordCount} Wörter
          {task.prompt?.wordNote ? ` · ${task.prompt.wordNote}` : ` (mindestens ${wordTarget.min})`}
        </p>
      </div>

      <div className="exam-submit-bar">
        <button
          type="button"
          className="exam-btn-primary"
          onClick={handleSubmitTask}
          disabled={submitting || !currentText.trim()}
        >
          {submitting
            ? 'Wird bewertet…'
            : activeTaskIdx < tasks.length - 1
              ? 'Teil abgeben → Weiter'
              : 'Prüfung abgeben'}
        </button>
      </div>
    </ExamPaperLayout>
  );
};

export default SchreibenPage;
